/**
 * Service Phase Tabs
 * WCAG-konforme Tab-Komponente für Service-Detail-Phasen.
 * Verwendet data-module="service-phase-tabs" und data-active-class (optional, Standard: is-active).
 * Panels werden mit GSAP ausgeblendet; Paragraphen pro Panel mit SplitText in Wörter geteilt und beim Wechsel animiert.
 */

import gsap from "@/lib/gsap";
import { reduced, SplitText } from "@/lib/gsap";
import { Scroll } from "@/lib/scroll";
import { onDestroy } from "@/modules/_";

function getDataElement(component, dataElement) {
  return component.querySelector(`[data-element="${dataElement}"]`);
}

export default function initServicePhaseTabs(element, _dataset) {
  const tabList = element.querySelector('[role="tablist"]');
  const tabButtons = tabList ? Array.from(tabList.querySelectorAll('button[role="tab"]')) : [];
  const tabPanels = Array.from(element.querySelectorAll('[role="tabpanel"]'));

  if (!tabList || tabButtons.length === 0 || tabPanels.length === 0) {
    console.warn("service-phase-tabs: tablist, tab buttons or tabpanels not found");
    return;
  }

  if (tabButtons.length !== tabPanels.length) {
    console.warn(
      `service-phase-tabs: Anzahl Tab-Buttons (${tabButtons.length}) stimmt nicht mit Anzahl Panels (${tabPanels.length}) überein. Bitte für jeden Tab ein [role="tabpanel"] im Markup anlegen.`
    );
    return;
  }

  const instanceRealIndex =
    Array.from(document.querySelectorAll('[data-module="service-phase-tabs"]')).indexOf(element) + 1;
  const titleEl = getDataElement(element, "component-title");
  const activeClass = element.getAttribute("data-active-class") || "is-active";
  element.removeAttribute("data-active-class");

  const initClass = "is-initialized";
  let currentIndex = 0;

  /** Container der Tab-Panels – wird position: relative gesetzt, ausgeblendete Panels position: absolute */
  const panelContainer = tabPanels[0]?.parentElement;
  if (panelContainer) {
    gsap.set(panelContainer, { position: "relative" });
  }

  const hiddenPanelStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    width: "100%",
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none",
  };
  const visiblePanelStyle = {
    position: "static",
    opacity: 1,
    visibility: "visible",
    pointerEvents: "auto",
  };

  const wordAnimDuration = reduced ? 0 : 0.4;
  const wordAnimStagger = reduced ? 0 : 0.02;

  /** SplitText-Instanzen pro Panel: splitInstances[panelIndex] = [split1, split2, …] für alle <p> in .w-richtext */
  const splitInstances = [];

  /** Erstellt SplitText pro Panel – erst nach Font-Load ausführen, damit Metriken stimmen. */
  function createSplitInstances() {
    tabPanels.forEach((panel, idx) => {
      const richtext = panel.querySelector(".w-richtext");
      const paragraphs = richtext ? Array.from(richtext.querySelectorAll("p")) : [];
      const splits = [];

      if (paragraphs.length > 0 && typeof SplitText !== "undefined") {
        paragraphs.forEach((paragraph) => {
          try {
            const split = new SplitText(paragraph, { type: "words" });
            splits.push(split);
            if (idx > 0) {
              gsap.set(split.words, { opacity: 0 });
            }
          } catch (e) {
            // Einzelnen Absatz überspringen
          }
        });
      }
      splitInstances.push(splits);
    });
  }

  /**
   * Animiert alle Wörter eines Panels ein (alle <p> in .w-richtext).
   * @param {number} panelIndex – Index des Panels
   */
  function animateWordsIn(panelIndex) {
    const splits = splitInstances[panelIndex];
    if (!splits || splits.length === 0) return;

    const allWords = splits.flatMap((split) => (split.words ? Array.from(split.words) : []));
    if (allWords.length === 0) return;

    if (reduced) {
      gsap.set(allWords, { opacity: 1 });
      return;
    }
    gsap.fromTo(
      allWords,
      { opacity: 0, y: 8 },
      {
        opacity: 1,
        y: 0,
        duration: wordAnimDuration,
        stagger: wordAnimStagger,
        ease: "power2.out",
      }
    );
  }

  /**
   * Scrollt die Tablist horizontal so, dass der fokussierte Tab sichtbar ist (für Tastatursteuerung).
   * @param {HTMLElement} tabButton – der Tab-Button, der sichtbar sein soll
   */
  function scrollTabIntoView(tabButton) {
    if (!tabButton) return;
    tabButton.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }

  /**
   * Aktiviert den Tab an Position newIndex.
   * @param {number} newIndex – Index des Tabs
   * @param {boolean} shouldFocus – ob der Button Fokus erhalten soll
   */
  function activateTab(newIndex, shouldFocus) {
    const prevIndex = currentIndex;

    tabButtons.forEach((btn, i) => {
      const selected = i === newIndex;
      btn.setAttribute("aria-selected", selected);
      btn.tabIndex = selected ? 0 : -1;
      btn.classList.toggle(activeClass, selected);
    });
    if (shouldFocus) {
      tabButtons[newIndex].focus({ preventScroll: true });
      scrollTabIntoView(tabButtons[newIndex]);
    }

    // Altes Panel ausblenden (aus Layoutfluss + unsichtbar)
    tabPanels[prevIndex].hidden = true;
    tabPanels[prevIndex].classList.remove(activeClass);
    gsap.set(tabPanels[prevIndex], hiddenPanelStyle);

    // Neues Panel einblenden (wieder im Layoutfluss) und Wörter animieren
    tabPanels[newIndex].hidden = false;
    tabPanels[newIndex].classList.add(activeClass);
    gsap.set(tabPanels[newIndex], visiblePanelStyle);
    animateWordsIn(newIndex);

    if (Scroll && typeof Scroll.resize === "function") {
      Scroll.resize();
    }
    currentIndex = newIndex;
  }

  // ARIA-IDs setzen
  if (titleEl) {
    titleEl.id = `tablist-${instanceRealIndex}`;
    tabList.setAttribute("aria-labelledby", titleEl.id);
  }

  tabButtons.forEach((tabButton, index) => {
    const tabId = `tab-${instanceRealIndex}-${index + 1}`;
    const panelId = `tabpanel-${instanceRealIndex}-${index + 1}`;

    tabButton.id = tabId;
    tabButton.setAttribute("aria-controls", panelId);
    tabPanels[index].id = panelId;
    tabPanels[index].setAttribute("aria-labelledby", tabId);

    if (index === 0) {
      tabButton.setAttribute("aria-selected", "true");
      tabButton.tabIndex = 0;
      tabButton.classList.add(activeClass);
      tabPanels[index].classList.add(activeClass);
    } else {
      tabButton.setAttribute("aria-selected", "false");
      tabButton.tabIndex = -1;
      tabButton.classList.remove(activeClass);
      tabPanels[index].classList.remove(activeClass);
      tabPanels[index].hidden = true;
    }
  });

  // Panels bis auf das erste mit GSAP ausblenden (aus Layoutfluss + unsichtbar)
  tabPanels.forEach((panel, idx) => {
    gsap.set(panel, idx === 0 ? visiblePanelStyle : hiddenPanelStyle);
  });

  // SplitText erst nach Font-Load, damit keine „SplitText called before fonts loaded“-Warnung
  function initSplitsAndActivate() {
    createSplitInstances();
    activateTab(0, false);
  }
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    document.fonts.ready.then(initSplitsAndActivate);
  } else {
    initSplitsAndActivate();
  }

  /** Nach einem Drag gesetzt, damit der folgende Click den Tab nicht aktiviert */
  let suppressNextClick = false;

  const handleClick = (e) => {
    if (suppressNextClick) {
      suppressNextClick = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const i = tabButtons.indexOf(e.currentTarget);
    if (i !== -1) activateTab(i, false);
  };

  const handleKeydown = (e) => {
    const focused = tabButtons.indexOf(document.activeElement);
    if (focused === -1) return;

    let target = null;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        target = (focused + 1) % tabButtons.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        target = (focused - 1 + tabButtons.length) % tabButtons.length;
        break;
      case "Home":
        target = 0;
        break;
      case "End":
        target = tabButtons.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    activateTab(target, true);
  };

  tabButtons.forEach((btn, i) => btn.addEventListener("click", handleClick));
  tabList.addEventListener("keydown", handleKeydown);

  /** Horizontaler Scroll-Container der Tablist (Tablist selbst oder scrollbarer Parent) */
  const getTablistScrollContainer = () => {
    let el = tabList;
    while (el) {
      const style = getComputedStyle(el);
      const overflowX = style.overflowX || style.overflow;
      if (overflowX === "auto" || overflowX === "scroll" || overflowX === "overlay") return el;
      if (el.scrollWidth > el.clientWidth) return el;
      el = el.parentElement;
    }
    return tabList;
  };
  const tablistScrollContainer = getTablistScrollContainer();

  /** Maus-Drag zum horizontalen Scrollen der Tablist (Desktop), auch über Buttons – rAF für flüssige Updates, Snap beim Loslassen */
  const DRAG_THRESHOLD_PX = 5;
  let dragStartX = 0;
  let dragStartScrollLeft = 0;
  let latestClientX = 0;
  let isDragging = false;
  let didDrag = false;
  let dragRafId = null;

  const applyDragScroll = () => {
    dragRafId = null;
    if (!isDragging) return;
    const dx = dragStartX - latestClientX;
    tablistScrollContainer.scrollLeft = dragStartScrollLeft + dx;
  };

  const handleTablistMouseDown = (e) => {
    isDragging = true;
    didDrag = false;
    dragStartX = e.clientX;
    latestClientX = e.clientX;
    dragStartScrollLeft = tablistScrollContainer.scrollLeft;
    tablistScrollContainer.style.cursor = "grabbing";
    tablistScrollContainer.style.userSelect = "none";
    document.addEventListener("mousemove", handleTablistMouseMove, { capture: true });
    document.addEventListener("mouseup", handleTablistMouseUp);
  };

  const handleTablistMouseMove = (e) => {
    if (!isDragging) return;
    const dx = dragStartX - e.clientX;
    if (!didDrag && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      didDrag = true;
    }
    if (didDrag) {
      e.preventDefault();
      latestClientX = e.clientX;
      if (dragRafId == null) {
        dragRafId = requestAnimationFrame(applyDragScroll);
      }
    }
  };

  /**
   * Findet den nächsten Tab für Scroll-Snap anhand der tatsächlich sichtbaren Viewport-Position
   * (getBoundingClientRect), damit es unabhängig von Scroll-Container vs. Tablist funktioniert.
   */
  function getNearestTabScrollLeft() {
    const containerWidth = tablistScrollContainer.clientWidth;
    const maxScroll = tablistScrollContainer.scrollWidth - containerWidth;
    if (maxScroll <= 0) return tablistScrollContainer.scrollLeft;

    const containerRect = tablistScrollContainer.getBoundingClientRect();
    const viewportCenterX = containerRect.left + containerWidth / 2;

    let nearestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < tabButtons.length; i++) {
      const tab = tabButtons[i];
      const tabRect = tab.getBoundingClientRect();
      const tabCenterX = tabRect.left + tabRect.width / 2;
      const dist = Math.abs(tabCenterX - viewportCenterX);
      if (dist < bestDist) {
        bestDist = dist;
        nearestIdx = i;
      }
    }

    const tab = tabButtons[nearestIdx];
    const tabRect = tab.getBoundingClientRect();
    const scrollLeft = tablistScrollContainer.scrollLeft;
    const tabCenterInContent = scrollLeft + (tabRect.left - containerRect.left) + tab.offsetWidth / 2;
    const targetScrollLeft = tabCenterInContent - containerWidth / 2;
    return Math.max(0, Math.min(Math.round(targetScrollLeft), maxScroll));
  }

  const handleTablistMouseUp = () => {
    if (!isDragging) return;
    if (dragRafId != null) {
      cancelAnimationFrame(dragRafId);
      dragRafId = null;
    }
    if (didDrag) {
      suppressNextClick = true;
      const snapLeft = getNearestTabScrollLeft();
      tablistScrollContainer.scrollTo({ left: snapLeft, behavior: "auto" });
    }
    isDragging = false;
    tablistScrollContainer.style.cursor = "";
    tablistScrollContainer.style.userSelect = "";
    document.removeEventListener("mousemove", handleTablistMouseMove, { capture: true });
    document.removeEventListener("mouseup", handleTablistMouseUp);
  };

  tabList.addEventListener("mousedown", handleTablistMouseDown);

  element.classList.add(initClass);

  onDestroy(() => {
    tabButtons.forEach((btn) => btn.removeEventListener("click", handleClick));
    tabList.removeEventListener("keydown", handleKeydown);
    tabList.removeEventListener("mousedown", handleTablistMouseDown);
    handleTablistMouseUp();
    document.removeEventListener("mousemove", handleTablistMouseMove, { capture: true });
    document.removeEventListener("mouseup", handleTablistMouseUp);
    element.classList.remove(initClass);
    splitInstances.forEach((splits) => {
      splits.forEach((split) => {
        if (split && typeof split.revert === "function") split.revert();
      });
    });
  });
}
